import { useEffect, useState } from "react";

import { Flex, Typography, Table, Switch, Button, Tooltip } from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined } from "@ant-design/icons";
import { useAlerts } from "../../providers/AlertProvider";
import { useLogs } from "../../providers/LogProvider";
import { useAuth } from "../../providers/AuthProvider";
import { AlertType } from "../../providers/types";
import { StyledOverlay } from "./styles";
import { ManageAlert } from "../../components/ManageAlert";
import { MODE_CREATE, MODE_EDIT, TYPE_SLACK } from "./consts";

const { Title } = Typography;

type paramsType = {
  emails?: string[];
  webhookUrl?: string;
};
export const Alerts = () => {
  const [mode, setMode] = useState<string>(MODE_CREATE);

  const [popupVisible, setPopupVisible] = useState<boolean>(false);
  const { alerts, getAlerts, createAlert, deleteAlert, editAlert } =
    useAlerts();
  const { apps, planDetails } = useLogs();
  const { mainApp } = useAuth();
  const [activeAlert, setActiveAlert] = useState<AlertType | null>(null);

  const handlePopupOpen = (open: boolean, mode?: string) => {
    if (open) {
      setMode(mode || MODE_CREATE);

      setPopupVisible(true);
    } else {
      setActiveAlert(null);
      setPopupVisible(false);
    }
  };

  const handleAlertCreation = (values: {
    appId: string;
    emails: string[];
    text: string;
    webhookUrl?: string;
    channelType: string;
  }) => {
    handlePopupOpen(false);
    const emails = values.emails;

    const params: paramsType = {};
    if (values.channelType === TYPE_SLACK && values.webhookUrl) {
      params.webhookUrl = values.webhookUrl;
    } else {
      params.emails = emails;
    }

    if (mode === MODE_EDIT && activeAlert) {
      editAlert({
        ...activeAlert,
        text: values.text,
        appId: values.appId,
        checkType: 1,
        isActive: activeAlert.isActive,
        channels: [
          {
            channelType: parseInt(values.channelType),
            params,
          },
        ],
      });
    } else {
      createAlert({
        appId: values.appId,
        text: values.text,
        checkType: 1,
        channels: [
          {
            channelType: parseInt(values.channelType),
            params,
          },
        ],
      });
    }
  };

  const handleDelete = () => {
    if (activeAlert) {
      handlePopupOpen(false);
      deleteAlert(activeAlert.id);
    }
  };

  useEffect(() => {
    if (alerts === null) {
      getAlerts();
    }
  }, [alerts]);

  const columns = [
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean, row: AlertType) => (
        <Switch
          checkedChildren={<CheckOutlined />}
          unCheckedChildren={<CloseOutlined />}
          defaultChecked={isActive}
          onChange={(checked) => {
            return editAlert({ ...row, isActive: checked });
          }}
        />
      ),
    },
    {
      title: "Application",
      dataIndex: "appId",
      key: "appId",
      render: (appId: string) => {
        if (!apps) return null;
        const app = apps.find((app) => app.id === appId);
        return app?.name;
      },
    },
    {
      title: "Text",
      dataIndex: "text",
      key: "text",
    },
    {
      title: "Recipients",
      dataIndex: "recipients",
      key: "recipients",
      render: (_: string, row: AlertType) => {
        if (row.channels[0].channelType.toString() === TYPE_SLACK) {
          return row.channels[0].params.webhookUrl;
        }
        return (row.channels[0].params.emails as string[])
          .map((el) => el)
          .join(", ");
      },
    },
    {
      title: "Last triggered",
      dataIndex: "lastTriggered",
      key: "lastTriggered",
      render: (lastTriggered: Date | null) => {
        return lastTriggered ? new Date(lastTriggered).toLocaleString() : "-";
      },
    },
    {
      title: "Edit",
      dataIndex: "edit",
      key: "edit",
      render: (_: string, record: AlertType) => {
        return (
          <div
            style={{ cursor: "pointer" }}
            onClick={() => [
              setActiveAlert(record),
              handlePopupOpen(true, MODE_EDIT),
            ]}
          >
            <EditOutlined />
          </div>
        );
      },
    },
  ];

  if (planDetails?.alerts === 0 && (alerts?.length === 0 || alerts === null)) {
    return (
      <Flex vertical align="center" justify="center" style={{ marginTop: 20 }}>
        <Title style={{ marginBottom: 20 }} level={4}>
          Alerts
        </Title>

        <>
          <Typography.Text type="secondary" style={{ marginBottom: 20 }}>
            Alerts feature is not available in your current plan. Please upgrade
            to a higher plan to use this feature.
          </Typography.Text>
          <a
            href={
              mainApp
                ? `https://dashboard.heroku.com/apps/${mainApp}/resources`
                : "https://elements.heroku.com/addons/logabyss"
            }
            target="_blank"
          >
            <Button type="primary">Upgrade</Button>
          </a>
        </>
      </Flex>
    );
  }
  return (
    <div>
      <Flex vertical align="center" justify="center" style={{ marginTop: 20 }}>
        <Title style={{ marginBottom: 20 }} level={4}>
          Alerts
        </Title>
        {planDetails?.alerts === 0 && (
          <>
            <Typography.Text type="secondary" style={{ marginBottom: 20 }}>
              Alerts feature is not available in your current plan. Please
              upgrade to a higher plan to use this feature.
            </Typography.Text>
            <a
              href={
                mainApp
                  ? `https://dashboard.heroku.com/apps/${mainApp}/resources`
                  : "https://elements.heroku.com/addons/logabyss"
              }
              target="_blank"
            >
              <Button type="primary">Upgrade</Button>
            </a>
          </>
        )}
        {alerts === null || alerts.length === 0 ? (
          <>
            <Typography.Text type="secondary" style={{ marginBottom: 20 }}>
              You do not have any alerts yet. Create one to get notified.
            </Typography.Text>
            <Button
              type="primary"
              onClick={() => handlePopupOpen(true, MODE_CREATE)}
            >
              Create alert
            </Button>
          </>
        ) : (
          <>
            <Flex
              justify="flex-start"
              style={{ width: "100%", marginBottom: 20 }}
            >
              {alerts.length >= (planDetails?.alerts || 0) ? (
                <Tooltip title="Alert limit reached. Upgrade your plan to create additional alerts.">
                  <Button
                    type="primary"
                    onClick={() => handlePopupOpen(true, MODE_CREATE)}
                    disabled
                  >
                    Create alert
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  type="primary"
                  onClick={() => handlePopupOpen(true, MODE_CREATE)}
                >
                  Create alert
                </Button>
              )}
            </Flex>
            {planDetails?.alerts === 0 ? (
              <StyledOverlay>
                <Table
                  style={{ width: "100%" }}
                  dataSource={alerts}
                  columns={columns}
                  pagination={false}
                />
              </StyledOverlay>
            ) : (
              <Table
                style={{ width: "100%" }}
                dataSource={alerts}
                columns={columns}
                pagination={false}
              />
            )}

            <Flex
              style={{
                marginTop: 50,
                marginBottom: 20,
                textAlign: "center",
              }}
            >
              <Typography.Text>
                {`You have a total of ${planDetails?.alerts} alerts in your plan, with ${
                  (planDetails?.alerts || 0) - alerts.length
                } alerts left to use.`}
                <br />
                {`We will send you up to ${planDetails?.emails} email notifications per day.`}
              </Typography.Text>
            </Flex>

            <Flex
              style={{
                marginBottom: 20,
                textAlign: "center",
              }}
            >
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Please note that each email notification sent counts towards
                this daily limit based on the number of recipients.
                <br /> This limit is experimental and may change at any time
                without notice.
              </Typography.Text>
            </Flex>
          </>
        )}
        {popupVisible && apps && (
          <ManageAlert
            handleAlertCreation={handleAlertCreation}
            onClose={() => handlePopupOpen(false)}
            mode={mode}
            handleDelete={handleDelete}
            activeAlert={activeAlert}
          />
        )}
      </Flex>
    </div>
  );
};

export default Alerts;
